import '../styles/index.css';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
// import 'office-ui-fabric-core/dist/css/fabric.min.css';
// comment

import { LayoutProvider } from '@shared/contexts';
import ProvidersWrapper from './ProvidersWrapper.tsx';

// -- Sentry
// @ts-ignore
import { env } from '../config/env.js';
Sentry.init({
  dsn: env.VITE_SENTRY_DSN,
  integrations: [Sentry.browserTracingIntegration()],
  environment: env.VITE_ENV,

  tracesSampleRate: 1.0,
});

// --- Pages
import { Home, Dev, NoPage } from '@shared/pages';
import { Folder, Product, Countries, ArchivePage, AllProducts } from '@modules/items';
import { RegulatoryTags } from '@modules/tags';
import {
  ReferenceDetection,
  ReferenceDetectionSummary,
  InlineBlockDetection,
  InlineBlockDetectionSummary,
} from '@modules/detections';
// import InlineBlockDetectionSummary from '../modules/detections/pages/InlineBlockDetectionSummary.page.tsx';
import { ComplianceCheckResult } from '@modules/mocks/complianceCheck';
import { MockChangeCreate, MockChangeDetail, MockChanges } from '@modules/mocks/change';
import {
  ChangesDashboardPage,
  CreateChangePage,
  ChangeRequestPage,
  AssessmentPage,
  PlanPage,
  ExecutionPage,
  CompletionPage,
  WorkspaceRedirect,
  ChangeWorkspaceLayout,
  ChangeV2Provider,
} from '@modules/mocks/changeV2';
import {
  FindingDetailPage,
  ReviewDetailPage,
  ReviewProvider,
  ReviewSetupPage,
  ReviewsRegisterPage,
} from '@modules/mocks/review';
import { TrainingRecordsPage } from '@modules/mocks/training';
import { Projects, ProjectDetails } from '@modules/projects';
import { AllSubmissions, SingleSubmission } from '@modules/submissions';
import { File } from '@modules/files';
import { Document, setupGoogleTranslateFix } from '@modules/documents';
import { Replica } from '@modules/replicas';
import { ItemsDashboard } from '@modules/dashboard';
import { ComplianceDashboard } from '@modules/mocks';
import { InlineBlock } from '@modules/inlineBlocks';
import { Literatures, Literature } from '@modules/literatures';
import {
  ChangeTemplates,
  ChangeTemplate,
  ChangeTemplateGeneral,
  ChangeTemplateDocumentSection,
  ChangeTemplateAssessments,
} from '@modules/changeTemplates';
import { Registrations } from '@modules/registrations';
import { SearchResults } from '@modules/search';
import { UserManagement, SettingsLayout, AccessLogs } from '@modules/users';

// --- Router
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
} from 'react-router-dom';

export const router = createBrowserRouter(
  createRoutesFromElements(
    <Route
      element={
        <ProvidersWrapper>
          <Outlet />
        </ProvidersWrapper>
      }
    >
      <Route path="/" element={<LayoutProvider />}>
        <Route path="/" element={<Home />} />
        <Route path="/products" element={<AllProducts />} />
        <Route path="/product/:id" element={<Product />} />
        <Route path="/countries" element={<Countries />} />
        <Route path="/registrations" element={<Registrations />} />
        <Route path="/product/:id/tags" element={<RegulatoryTags />} />

        <Route path="/product/:productId/reference-detection" element={<ReferenceDetection />} />
        <Route path="/product/:productId/reference-detection/:runId/summary" element={<ReferenceDetectionSummary />} />

        <Route path="/product/:productId/library-detection" element={<InlineBlockDetection />} />
        <Route path="/product/:productId/library-detection/:runId/summary" element={<InlineBlockDetectionSummary />} />

        <Route path="/product/:productId/submissions" element={<AllSubmissions />} />
        <Route path="/product/:productId/submissions/:submissionId" element={<SingleSubmission />} />
        <Route
          path="/product/:productId/submissions/:submissionId/compliance-check"
          element={<ComplianceCheckResult />}
        />
        <Route path="/product/:productId/changes/create" element={<MockChangeCreate />} />
        <Route path="/product/:productId/changes/:changeNumber" element={<MockChangeDetail />} />
        <Route path="/product/:productId/changes" element={<MockChanges />} />

        <Route path="/folder/:id" element={<Folder />} />
        <Route path="/document/:id" element={<Document />} />
        <Route path="/documents/:id" element={<Document />} />
        <Route path="/file/:id" element={<File />} />
        <Route path="/inline-block/:id" element={<InlineBlock />} />
        <Route path="/literatures" element={<Literatures />} />
        <Route path="/literature/:id" element={<Literature />} />
        <Route path="/change-templates" element={<ChangeTemplates />} />
        <Route path="/change-template/:id" element={<ChangeTemplate />}>
          <Route index element={<ChangeTemplateGeneral />} />
          <Route path="request" element={<ChangeTemplateDocumentSection />} />
          <Route path="assessments" element={<ChangeTemplateAssessments />} />
          <Route path="assessments/:assessmentId" element={<ChangeTemplateDocumentSection />} />
        </Route>
        <Route path="/changes/create" element={<MockChangeCreate />} />
        <Route path="/changes/:changeNumber" element={<MockChangeDetail />} />
        <Route path="/changes" element={<MockChanges />} />

        {/* Change management V2 prototype (separate from the V1 change mock above). */}
        <Route
          element={
            <ChangeV2Provider>
              <Outlet />
            </ChangeV2Provider>
          }
        >
          <Route path="/change-v2" element={<ChangesDashboardPage />} />
          <Route path="/change-v2/new" element={<CreateChangePage />} />
          <Route path="/change-v2/:changeId" element={<ChangeWorkspaceLayout />}>
            <Route index element={<WorkspaceRedirect />} />
            <Route path="request" element={<ChangeRequestPage />} />
            <Route path="triage" element={<Navigate to="../request" replace />} />
            <Route path="assessment" element={<AssessmentPage />} />
            <Route path="approval-to-proceed" element={<Navigate to="../assessment" replace />} />
            <Route path="plan" element={<PlanPage />} />
            <Route path="execution" element={<ExecutionPage />} />
            <Route path="completion" element={<CompletionPage />} />
            <Route path="release" element={<Navigate to="../completion" replace />} />
            <Route path="effectiveness" element={<Navigate to="../completion" replace />} />
            <Route path="closure" element={<Navigate to="../completion" replace />} />
          </Route>
        </Route>
        {/* Pre-submission review prototype. One register for the organization, scoped
            to a product through the productId query param. */}
        <Route
          element={
            <ReviewProvider>
              <Outlet />
            </ReviewProvider>
          }
        >
          <Route path="/review" element={<ReviewsRegisterPage />} />
          <Route path="/review/new" element={<ReviewSetupPage />} />
          <Route path="/review/:reviewId" element={<ReviewDetailPage />} />
          <Route path="/review/:reviewId/findings/:findingId" element={<FindingDetailPage />} />
        </Route>
        {/* Training records prototype; renders NoPage unless mock.training is enabled. */}
        <Route path="/training" element={<TrainingRecordsPage />} />
        <Route path="/projects/:projectsFolderId" element={<Projects />} />
        <Route path="/project/:projectId" element={<ProjectDetails />} />
        <Route path="/synced/:id" element={<Replica />} />
        <Route path="/settings" element={<SettingsLayout />}>
          {/* redirect /settings → /settings/user-management */}
          <Route index element={<Navigate to="user-management" replace />} />

          <Route path="user-management" element={<UserManagement />} />
          <Route path="archive" element={<ArchivePage />} />
          <Route path="access-logs" element={<AccessLogs />} />
        </Route>
        <Route path="/search" element={<SearchResults />} />
        <Route path="/dev" element={<Dev />} />
        <Route path="/compliance" element={<ComplianceDashboard />} />
        <Route path="/dashboard" element={<ItemsDashboard />} />
        <Route path="*" element={<NoPage />} />
      </Route>
    </Route>
  ),
  {
    // Opted in early so the router stops warning on every load. In v7 a relative path inside a splat
    // route resolves from the splat's own path rather than from the route that matched it. This app
    // has one splat route, `path="*"`, whose element renders no relative links - so nothing moves.
    future: { v7_relativeSplatPath: true },
  }
);

// Set up the fix BEFORE React renders
setupGoogleTranslateFix();

ReactDOM.createRoot(document.getElementById('root')!).render(
  // The other half of the v7 opt-in; this flag lives on the provider, not on the router. It wraps
  // router state updates in React.startTransition, which is v7's default.
  <RouterProvider router={router} future={{ v7_startTransition: true }} />
);

/*
  <React.StrictMode>
  </React.StrictMode>
*/
